import type { TrackTeamTrialUpgradeRequestSubmit } from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import type { QueryUserResponse } from '@heidi/workspace/data';
import { useCombinedQueriesTeamGeneralInfo, useQueryUser } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { Send } from 'lucide-react';
import { forwardRef, useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import {
  type TeamTrialUpgradeRequestPlan,
  openTeamTrialUpgradeRequestModalAtom,
} from '../team-trial-upgrade-request-modal/atoms';
import { teamTrialUpgradeRequestButtonMessages } from './messages';

function selectContactedAdminSubscriptionUpgradeTeamIds(
  user: QueryUserResponse
): string[] | undefined {
  return user.contacted_admin_for_subscription_upgrade_team_ids;
}

function getHasRequestedTeamPlanUpgrade(
  teamId: string | undefined,
  contactedAdminSubscriptionUpgradeTeamIds: string[] | undefined
): boolean {
  return Boolean(teamId && contactedAdminSubscriptionUpgradeTeamIds?.includes(teamId));
}

export type TeamTrialUpgradeRequestButtonPlan = TeamTrialUpgradeRequestPlan;

/**
 * Who opens the globally mounted request modal, and therefore who owns the
 * submit event's `screen_name`.
 *
 * A union rather than two optional props, because the two are genuinely
 * exclusive and used to be silently mixed: every caller that supplied
 * `onOpenTeamRequestModal` also passed a `screenName` this control then ignored
 * — four dead prop passes that nothing could see. The submit reports the
 * ORIGIN's screen (see `atoms.ts`), so the emitter is only this control's
 * business on the path where this control is the origin.
 */
type TeamTrialUpgradeRequestButtonOpenProps =
  | {
      /**
       * This control opens the modal itself via
       * `openTeamTrialUpgradeRequestModalAtom`, so it carries the submit emitter
       * bound to the screen it is rendered on.
       */
      trackRequestSubmitted: TrackTeamTrialUpgradeRequestSubmit;
      onOpenTeamRequestModal?: never;
    }
  | {
      /**
       * The parent opens the modal from this callback (e.g. after hiding the
       * host modal) and binds the emitter itself, so this control needs none.
       */
      onOpenTeamRequestModal: () => void;
      trackRequestSubmitted?: never;
    };

export type TeamTrialUpgradeRequestButtonProps = {
  plan: TeamTrialUpgradeRequestPlan;
  /** Overrides the default "Request to upgrade" label when the host surface uses different copy. */
  requestToUpgradeMessage?: MessageDescriptor;
  /**
   * When set and the user had already requested an upgrade before this control
   * mounted, keeps the primary CTA label and shows this tooltip on the disabled
   * button. A request submitted during this visit still switches to "Request
   * sent to your admin".
   */
  requestedTooltipMessage?: MessageDescriptor;
} & TeamTrialUpgradeRequestButtonOpenProps;

/**
 * Primary-style button for non-admin team members during the end-of-trial recap experience
 * instead of a self-serve upgrade CTA. Opens the team upgrade request modal until a request
 * has been sent, then shows a disabled confirmation state.
 */
export const TeamTrialUpgradeRequestButton = forwardRef<
  HTMLButtonElement,
  TeamTrialUpgradeRequestButtonProps
>(function TeamTrialUpgradeRequestButton(
  {
    plan,
    trackRequestSubmitted,
    requestToUpgradeMessage,
    requestedTooltipMessage,
    onOpenTeamRequestModal,
  },
  ref
) {
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = useMemo(() => teamDetails?.team?.id, [teamDetails]);
  const contactedAdminTeamIdsQuery = useQueryUser(
    selectContactedAdminSubscriptionUpgradeTeamIds
  );
  const hasRequested = useMemo(
    () => getHasRequestedTeamPlanUpgrade(teamId, contactedAdminTeamIdsQuery.data),
    [teamId, contactedAdminTeamIdsQuery.data]
  );
  const useExternalRequestModal = Boolean(onOpenTeamRequestModal);
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );

  const buttonVariant = plan === SubscriptionTier.CLINICIAN_PRO_PLUS ? 'pro_plus' : 'pro';
  const requestToUpgradeLabel =
    requestToUpgradeMessage ?? teamTrialUpgradeRequestButtonMessages.requestToUpgrade;

  const hadRequestedBeforeOpenRef = useRef<boolean | null>(null);
  if (
    hadRequestedBeforeOpenRef.current === null &&
    teamId !== undefined &&
    contactedAdminTeamIdsQuery.data !== undefined
  ) {
    hadRequestedBeforeOpenRef.current = hasRequested;
  }
  const hadRequestedBeforeOpen = hadRequestedBeforeOpenRef.current ?? false;
  const showPreExistingRequestState =
    hasRequested && hadRequestedBeforeOpen && Boolean(requestedTooltipMessage);

  const handleOpenRequestModal = useCallback(() => {
    if (useExternalRequestModal || !trackRequestSubmitted) {
      onOpenTeamRequestModal?.();
    } else {
      openTeamTrialUpgradeRequestModal({
        plan,
        trackRequestSubmitted,
      });
    }
  }, [
    onOpenTeamRequestModal,
    openTeamTrialUpgradeRequestModal,
    plan,
    trackRequestSubmitted,
    useExternalRequestModal,
  ]);

  const planLogo =
    plan === SubscriptionTier.CLINICIAN_PRO_PLUS ? (
      <TierPracticeFilled className="size-4 shrink-0" aria-hidden />
    ) : (
      <TierProFilled className="size-4 shrink-0" />
    );

  const button = (
    <Button
      ref={ref}
      type="button"
      variant={buttonVariant}
      size="md"
      className="w-full"
      disableAutomaticSvgSizing
      disabled={hasRequested}
      onClick={handleOpenRequestModal}
      data-testid="team-trial-upgrade-request-button"
    >
      {showPreExistingRequestState || !hasRequested ? (
        <>
          {planLogo}
          <FormattedMessage {...requestToUpgradeLabel} />
        </>
      ) : (
        <>
          <Send className="size-4 shrink-0 stroke-icon-md" aria-hidden />
          <FormattedMessage
            {...teamTrialUpgradeRequestButtonMessages.requestSentToAdmin}
          />
        </>
      )}
    </Button>
  );

  if (!showPreExistingRequestState || !requestedTooltipMessage) {
    return button;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Wrapper receives pointer events when the inner button is disabled. */}
          <span className="flex w-full">{button}</span>
        </TooltipTrigger>
        <TooltipContent>
          <FormattedMessage {...requestedTooltipMessage} />
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
